import clsx from "clsx";
import { Controller, useWatch, type FieldPath, type FieldValues } from "react-hook-form";
import { useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";

import { PHONE_LENGTHS } from "@/src_new/shared/config/contact";
import {
  MOBILE_COUNTRY_CODES,
  cleanMobileCountryCode,
  validateMobileNumber,
} from "@/src_new/shared/config/mobileCountryCodes";
import { getThemeClass, getThemeComponentGroup } from "@/src_new/shared/lib/themeComponentClasses";
import { App_Url_New } from "@/src_new/shared/common/static";
import { axiosInstance } from "@/src_new/shared/lib/axiosInstance";
import {
  setMobileCountryCode,
  type MobileCountryCode,
} from "@/src_new/shared/redux/reducers/mobileCountryCodeSlice";
import type { AppDispatch } from "@/src_new/shared/redux/store/store";
import { usePosterReducers } from "@/src_new/shared/redux/store/useGetData";

import { SelectInput } from "./SelectInput";
import { inputClassName } from "./TextInput";
import type { BaseInputProps } from "@/src_new/shared/types";
import { WrapperInput } from "./WrapperInput";

const normalizeCountryCodeResponse = (payload: unknown): MobileCountryCode[] => {
  const data = (payload as any)?.data;
  const list = Array.isArray(data) ? data : Array.isArray(payload) ? payload : [];

  return list
    .filter((item: any) =>
      cleanMobileCountryCode(item?.mobile_country_code || item?.mobileCountryCode)
    )
    .map((item: any) => ({
      id: item.id,
      country_code: String(item.country_code || item.countryCode || ""),
      mobile_country_code: String(item.mobile_country_code || item.mobileCountryCode || ""),
      regex_pattern: item.regex_pattern || item.regexPattern,
    }));
};

const getQuantifierLength = (pattern: string, index: number) => {
  const match = pattern.slice(index).match(/^\{(\d+)(?:,(\d+))?\}/);

  if (!match) {
    return { length: 1, nextIndex: index };
  }

  return {
    length: Number(match[2] || match[1]),
    nextIndex: index + match[0].length,
  };
};

const countPatternDigitLength = (pattern: string): number | null => {
  let length = 0;

  for (let index = 0; index < pattern.length; index += 1) {
    const current = pattern[index];
    const next = pattern[index + 1];

    if (current === "\\" && next === "d") {
      const quantifier = getQuantifierLength(pattern, index + 2);
      length += quantifier.length;
      index = quantifier.nextIndex - 1;
      continue;
    }

    if (current === "[") {
      const closingIndex = pattern.indexOf("]", index);
      if (closingIndex === -1) return null;

      const quantifier = getQuantifierLength(pattern, closingIndex + 1);
      length += quantifier.length;
      index = quantifier.nextIndex - 1;
      continue;
    }

    if (current === "(") {
      const closingIndex = pattern.indexOf(")", index);
      if (closingIndex === -1) return null;

      const group = pattern.slice(index + 1, closingIndex);
      const longestOption = Math.max(
        ...group.split("|").map((option) => countPatternDigitLength(option) || 0)
      );
      const quantifier = getQuantifierLength(pattern, closingIndex + 1);
      length += longestOption * quantifier.length;
      index = quantifier.nextIndex - 1;
      continue;
    }

    if (/\d/.test(current)) {
      const quantifier = getQuantifierLength(pattern, index + 1);
      length += quantifier.length;
      index = quantifier.nextIndex - 1;
    }
  }

  return length || null;
};

const formatCountryCodeValue = (country: MobileCountryCode) =>
  country.mobile_country_code.startsWith("+")
    ? country.mobile_country_code
    : `+${country.mobile_country_code}`;

const getMobileLocalDigitLength = (country?: MobileCountryCode, fallbackCountryCode?: string) => {
  const countryCode =
    cleanMobileCountryCode(country?.mobile_country_code) ||
    cleanMobileCountryCode(fallbackCountryCode);

  if (!country?.regex_pattern || !countryCode) {
    return PHONE_LENGTHS[countryCode] || 15;
  }

  const prefixPattern = new RegExp(`^\\\\?\\+${countryCode}`);
  const escapedDialCode = `\\${country.mobile_country_code.startsWith("+") ? "" : "+"}${country.mobile_country_code}`;
  const patternBody = country.regex_pattern.replace(/^\^/, "").replace(/\$$/, "");
  const localPattern = (
    patternBody.startsWith(escapedDialCode)
      ? patternBody.slice(escapedDialCode.length)
      : patternBody.replace(prefixPattern, "")
  ).replace(/\[[^\]]+\]\?/g, (token) => (/\\s|\s|\\-|-/.test(token) ? "" : token));

  return countPatternDigitLength(localPattern) || PHONE_LENGTHS[countryCode] || 15;
};

const syncPhoneLengthsFromCountryList = (countryList: MobileCountryCode[]) => {
  countryList.forEach((country) => {
    const countryCode = cleanMobileCountryCode(country.mobile_country_code);
    const length = getMobileLocalDigitLength(country, countryCode);

    if (countryCode && length) {
      PHONE_LENGTHS[countryCode] = length;
    }
  });
};

type MobileNumberInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = BaseInputProps<TFieldValues, TName> & {
  countryCodeName?: FieldPath<TFieldValues>;
  numberName?: FieldPath<TFieldValues>;
  defaultCountryCode?: string;
  columnsClassName?: string;
};

export function MobileNumberInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  disabled,
  required,
  className,
  countryCodeName,
  numberName,
  defaultCountryCode = "971",
  columnsClassName = "grid-cols-1 sm:grid-cols-[3.5fr_6.5fr]",
}: MobileNumberInputProps<TFieldValues, TName>) {
  const dispatch = useDispatch<AppDispatch>();
  const inputClasses = getThemeComponentGroup("input");
  const { mobileCountryCode: apiCountryList = [] } = usePosterReducers();
  const countryList = apiCountryList.length > 0 ? apiCountryList : MOBILE_COUNTRY_CODES;
  const countryCodePath = countryCodeName ?? (`${name}.countryCode` as FieldPath<TFieldValues>);
  const numberPath = numberName ?? (`${name}.number` as FieldPath<TFieldValues>);
  const countryCode = useWatch({
    control,
    name: countryCodePath,
  }) as string | undefined;
  const countryCodeRef = useRef(countryCode || defaultCountryCode);
  const selectedCountry = useMemo(
    () =>
      countryList.find(
        (country) =>
          cleanMobileCountryCode(country.mobile_country_code) ===
          (countryCode || defaultCountryCode)
      ),
    [countryCode, countryList, defaultCountryCode]
  );
  const maxLength = getMobileLocalDigitLength(selectedCountry, countryCode || defaultCountryCode);

  useEffect(() => {
    countryCodeRef.current = countryCode || defaultCountryCode;
  }, [countryCode, defaultCountryCode]);

  useEffect(() => {
    if (apiCountryList.length > 0) {
      return;
    }

    let active = true;

    axiosInstance
      .get(App_Url_New.personaldetails.mobile_country_code)
      .then((response) => {
        if (!active) {
          return;
        }

        const nextCountryList = normalizeCountryCodeResponse(response.data);
        syncPhoneLengthsFromCountryList(nextCountryList);
        dispatch(setMobileCountryCode(nextCountryList));
      })
      .catch((error) => {
        console.error("Failed to fetch mobile country codes:", error);
      });

    return () => {
      active = false;
    };
  }, [apiCountryList.length, dispatch]);

  useEffect(() => {
    syncPhoneLengthsFromCountryList(countryList);
  }, [countryList]);

  const { countryOptions, dialCodeByValue } = useMemo(() => {
    const grouped = new Map<string, { dialCode: string; countries: string[] }>();

    countryList.forEach((country) => {
      const value = cleanMobileCountryCode(country.mobile_country_code);

      if (!value) {
        return;
      }

      const existing = grouped.get(value);

      if (existing) {
        if (country.country_code && !existing.countries.includes(country.country_code)) {
          existing.countries.push(country.country_code);
        }
        return;
      }

      grouped.set(value, {
        dialCode: formatCountryCodeValue(country),
        countries: country.country_code ? [country.country_code] : [],
      });
    });

    return {
      countryOptions: Array.from(grouped.entries()).map(([value, { dialCode, countries }]) => ({
        value,
        label: countries.length ? `${countries.join("/")} (${dialCode})` : dialCode,
      })),
      // the closed field shows only the dial code, e.g. "+91"
      dialCodeByValue: new Map(
        Array.from(grouped.entries()).map(([value, { dialCode }]) => [value, dialCode])
      ),
    };
  }, [countryList]);

  return (
    <Controller
      control={control}
      name={numberPath}
      rules={{
        validate: (value) =>
          validateMobileNumber(String(value || ""), countryCodeRef.current, countryList),
      }}
      render={({ field, fieldState }) => (
        <WrapperInput
          label={label}
          error={fieldState.error?.message}
          description={description}
          required={required}
          className={className}
        >
          <div className={clsx("grid gap-3", columnsClassName)}>
            <SelectInput
              control={control}
              name={countryCodePath}
              label=""
              className="min-w-0"
              placeholder="Code"
              searchPlaceholder="Search"
              searchable
              menuClassName="min-w-[12rem]"
              disabled={disabled}
              options={countryOptions}
              getSelectedLabel={(option) =>
                dialCodeByValue.get(String(option.value)) ?? option.label
              }
              onValueChange={(value) => {
                countryCodeRef.current = String(value);
                field.onChange("");
              }}
            />

            <input
              {...field}
              value={field.value ?? ""}
              type="tel"
              inputMode="numeric"
              placeholder={`Enter ${maxLength} digits`}
              maxLength={maxLength}
              disabled={disabled}
              onChange={(event) => {
                const numericValue = event.target.value.replace(/\D/g, "").slice(0, maxLength);
                field.onChange(numericValue);
              }}
              className={clsx(
                inputClassName,
                "border min-w-0",
                getThemeClass(inputClasses.borderColor, "border-theme-border-strong"),
                getThemeClass(inputClasses.bgColor, "bg-theme-surface"),
                getThemeClass(inputClasses.textColor, "text-theme-text"),
                getThemeClass(
                  inputClasses.placeholderColor,
                  "placeholder:text-theme-text-placeholder"
                ),
                getThemeClass(inputClasses.focusBorderColor, "focus:border-theme-primary"),
                "focus:ring-2",
                getThemeClass(inputClasses.focusRingColor, "focus:ring-theme-primary/10"),
                inputClasses.base,
                fieldState.error &&
                  clsx(
                    inputClasses.errorBorderColor || "border-theme-danger-bright",
                    "focus:ring-theme-danger-bright/10"
                  )
              )}
            />
          </div>
        </WrapperInput>
      )}
    />
  );
}
